// Post.js
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';

import './Post.css';

import { canAccessStudio } from './studio/studioAccess';

import {
  createStudioAnalyticsEvent,
  trackStudioEvent,
} from './studio/avatar/services/studioAnalyticsApi';

import { useNavigate, Link, useLocation } from 'react-router-dom';
import { usePostContext } from './context/PostContext';
import { useSavedPosts } from './context/SavedPostsContext';
import ReadMoreText from './common/ReadMoreText';

import { BADGE_DISPLAY } from './constants/badges';
import {
  getPostStyleCssVars,
  isValidPostStyleTheme,
} from './constants/postStyleThemes';
import { parseMentions } from './utils/parseMentions';
import api from './api';
import { getStakeSummary } from './api';

import likeIcon from './assets/like-icon.png';
import likeIconRed from './assets/like-icon-red.png';
import commentIcon from './assets/comment-icon.png';
import shareIcon from './assets/share-icon.png';
import saveIcon from './assets/saved-icon.png';
import saveIconFilled from './assets/save-icon-filled.png';
import closeIcon from './assets/close-icon.png';
import deleteIcon from './assets/delete-outline-icon.png';
import reportIcon from './assets/report-icon.png';
import friendIcon from './assets/friend-icon.png';
import tipIcon from './assets/tip-icon.png';
import viewCountIcon from './assets/viewcount-icon.png';
import TipDrawer from './components/popups/TipDrawer';
import TipListDrawer from './components/popups/TipListDrawer';
import DeletePostDrawer from './components/popups/DeletePostDrawer';
import ShareDrawer from './components/popups/ShareDrawer';
import LikeDrawer from './components/popups/LikeDrawer';
import CommentDrawer from './components/popups/CommentDrawer';
import TxnDrawer from './components/popups/TxnDrawer';
import CreatePostPopup from './components/popups/CreatePostPopup';
import ReportPopup from './components/popups/ReportPopup';
import hiddenPlaceholder from './assets/hidden-placeholder.png';
import TipBanner from './components/banners/TipBanner'; // ✅
import backIcon from './assets/backspace-icon.png';
import PostImage from './components/media/PostImage';
import { useInViewport } from './hooks/useInViewport';
import usePostView from './hooks/usePostView';
import { POST_VIEW_COUNT_UPDATED_EVENT } from './services/postViewTracker';
import PostMediaGrid from './components/media/PostMediaGrid';
import PostVideo from './components/media/PostVideo';
import PostActionDrawer from './components/popups/PostActionDrawer';
import pinIcon from './assets/pin-icon.png';
import StakeDrawer from './components/popups/StakeDrawer';
import stakeBadgeIcon from './assets/stakebadge-icon.png';
import p2pIcon from './assets/p2p-icon.png';
import goldTickIcon from './assets/goldtick-icon.png';
import PostPollBlock from './components/PostPollBlock';
import QuickTopUpPopup from './components/popups/QuickTopUpPopup';
import BlockConfirmDrawer from './components/popups/BlockConfirmDrawer';
import ProductRepostPreview from './components/ProductRepostPreview';
import PostDestinationAction from './components/PostDestinationAction';

const classifyMediaUrl = (url = '') => {
  const u = String(url).toLowerCase().split('?')[0];
  if (u.endsWith('.mp4') || u.endsWith('.webm') || u.endsWith('.mov')) return 'video';
  if (u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.png') || u.endsWith('.gif') || u.endsWith('.webp')) return 'image';
  if (u.includes('/videos/')) return 'video';
  return 'image';
};


function formatViewCount(value) {
  const count = Math.max(0, Math.floor(Number(value) || 0));

  if (count < 1000) {
    return String(count);
  }

  if (count < 1000000) {
    const thousands = count / 1000;

    return `${Number(
      thousands.toFixed(thousands < 10 ? 1 : 0)
    )}K`;
  }

  const millions = count / 1000000;

  return `${Number(
    millions.toFixed(millions < 10 ? 1 : 0)
  )}M`;
}

// Track ownership across every mounted Post instance.
const postOverlayOwners = new Set();
const fullscreenPreviewOwners = new Set();


function Post({
  isActive = true,
  id,
  name,
  username,
  profilePic,
  location: postLocation,
  content,
  media,
  mediaType,
  thumbnail,
  transactionId,
  poll = null,
  onPollVoteUpdate,
  userType,
  isMerchant,
  isApp = false,
  accountType = 'primary',

  createdAt,

  updatedAt,
  editedAt,        // ✅ NEW (real edit timestamp)
  editCount,       // ✅ NEW (real edit counter)
  timestamp,


onDelete,
currentUser,
currentUserProfilePic = '',
currentScreen = 'home',
user = {},

  closeSignal,
  wrapperId,

  likesCount = 0,
  commentsCount = 0,
  viewCount = 0,
  userHasLiked = false,

  isHidden = false,
  hiddenReason = '',
  tips = [],  // ✅ ADD THIS SAFELY
  mentions = [], 
  postIndex,
  pinned,     // 🔝 comes from backend serializePost
  stakeActivePi = 0,
  stakeNextExpiryAt = null,
  canStake: canStakeProp,   // 👈 optional override from parent
  onStakeDetailsClick,      // 👈 NEW: open Stake Details drawer
  badgesBrief: badgesBriefProp = [],  // 👈 accept from parent

isRepost = false,
repostOf: repostOfPost = null,
repostComment = '',

// 🛒 Product repost
postType = 'post',
sharedProduct = null,
productSnapshot = null,
isSponsored = false,


  autoOpenTipDrawer = false,
  autoOpenTipAmount = 0.5,
  autoOpenTipTargetUsername = null,

  boostDrawerRequest = 0,
  // ✅ Paid/selected post background style

style = null,
backgroundTheme = 'normal',
hasPaidStyle = false,

// ✅ Optional destination CTA
postAction = null,

}) {


const resolvedBackgroundTheme =
  style?.backgroundTheme ||
  backgroundTheme ||
  'normal';

const safeBackgroundTheme = isValidPostStyleTheme(resolvedBackgroundTheme)
  ? resolvedBackgroundTheme
  : 'normal';

const hasStyledBackground = safeBackgroundTheme !== 'normal';

const postStyleVars = React.useMemo(
  () => getPostStyleCssVars(safeBackgroundTheme),
  [safeBackgroundTheme]
);


  // For reposts, main text is the user’s quote; original text goes in the embed
  const mainText = isRepost ? (repostComment || content || '') : (content || '');

  // 🔁 Full original post for repost embeds
  const [originalPost, setOriginalPost] = useState(null);

// If the original was deleted/hidden or 404s, show a placeholder instead of a broken card
const [originalMissing, setOriginalMissing] = useState(false);



// --- badge brief (prefer prop, else user.badgesBrief, else map user.badges) ---
  const badgesBrief = React.useMemo(() => {
    if (Array.isArray(badgesBriefProp) && badgesBriefProp.length) {
      return badgesBriefProp.slice(0, 3);
    }
    if (Array.isArray(user?.badgesBrief) && user.badgesBrief.length) {
      return user.badgesBrief.slice(0, 3);
    }
    // last resort: compress active full badges to ids
    if (Array.isArray(user?.badges) && user.badges.length) {
      const now = Date.now();
      const active = user.badges
        .filter(b => b?.type && (!b.expiresAt || new Date(b.expiresAt).getTime() > now))
        .map(b => String(b.type).toUpperCase());
      const seen = new Set();
      const PRIORITY = ["BARTER_KING","MERCHANT_STAR","CREATOR_FLAME","PI_GUARDIAN"];
      const ordered = [...active.filter(t => !seen.has(t) && (seen.add(t), true))]
        .sort((a,b) => PRIORITY.indexOf(a) - PRIORITY.indexOf(b));
      return ordered.slice(0, 3);
    }
    return [];
  }, [badgesBriefProp, user?.badgesBrief, user?.badges]);

  const BadgeGlyph = ({ id }) => {
    const cfg = BADGE_DISPLAY?.[id] || {};
    const label = cfg.label || id.replace(/_/g, ' ');
    if (cfg.icon) {
      return <img src={cfg.icon} alt={label} title={label} className="post-badge-icon" />;
    }
    if (cfg.emoji) {
      return <span className="post-badge-emoji" title={label} aria-label={label}>{cfg.emoji}</span>;
    }
    return null;
 };


  const location = useLocation(); // ✅ Safe now

// High-priority decode for the first few posts in the feed
  const isPriorityPost = (postIndex ?? 999) < 3;

// --- network selection (route param wins, else hostname) ---
const routeNetwork = React.useMemo(() => {
  const p = new URLSearchParams(location.search);
  const n = (p.get('network') || '').toLowerCase();
  return n === 'testnet' || n === 'mainnet' ? n : '';
}, [location.search]);

const effectiveNetwork = React.useMemo(() => {
  if (routeNetwork) return routeNetwork;
  if (typeof window !== 'undefined' && window.location.hostname.includes('testnet')) return 'testnet';
  return 'mainnet';
}, [routeNetwork]);

  const navigate = useNavigate();
  const videoRef = useRef(null);
  const autoTipOpenedRef = useRef(false);

const quickTopUpTimerRef = useRef(null);
const tipBannerTimerRef = useRef(null);

  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [tipMessage, setTipMessage] = useState('');
  const [tipType, setTipType] = useState(''); // 'success' | 'error'
  const [showActionDrawer, setShowActionDrawer] = useState(false);
  const [showStakeDrawer, setShowStakeDrawer] = useState(false); // will render next step

  const [showQuickTopUp, setShowQuickTopUp] = useState(false);
  const pushedFsHistoryRef = useRef(false);

  const closingFromButtonRef = useRef(false);
  const openingStudioRef = useRef(false);

// -- viewer/admin helpers --

const viewer = String(currentUser || '').toLowerCase();

const postViewRef = usePostView({
  postId: id,
  postAuthorUsername: username,
  currentViewerUsername: viewer,
  isSponsored,
  enabled: isActive && !isHidden,
});

const viewerIsAdmin =
  viewer === 'barterfi_admin' ||
  (Array.isArray(user?.roles) && user.roles.map(r => String(r).toLowerCase()).includes('admin'));

const canStakeToBoost = !!viewer; // any logged-in user can stake

// define this once, here (after viewer is defined)
const openStakeDrawer = () => {
  if (!viewer) return; // optionally show a toast
  setShowStakeDrawer(true);
};


const handledBoostRequestRef = useRef(null);

useEffect(() => {
  if (!boostDrawerRequest || !isActive || !viewer || !id) return;

  const requestKey = `${id}:${viewer}:${boostDrawerRequest}`;

  if (handledBoostRequestRef.current === requestKey) return;

  handledBoostRequestRef.current = requestKey;
  setShowStakeDrawer(true);
}, [boostDrawerRequest, isActive, viewer, id]);

// Save current Home feed scroll so we can restore when coming back
const saveHomeScrollPos = () => {
  try {
    const feed = document.querySelector('.feed-content');
    const y = feed ? feed.scrollTop : window.scrollY || 0;
    sessionStorage.setItem('bf_home_scrollTop', String(y));
    sessionStorage.setItem('bf_home_fromPostId', id); // optional anchor
  } catch {}
};



// screen-only toast (success | error) for report flow
const [reportToast, setReportToast] = useState({ visible: false, message: '', type: '' });
const showReportToast = (message, type = 'success', duration = 2200) => {
  setReportToast({ visible: true, message, type });
  window.clearTimeout(showReportToast._t);
  showReportToast._t = window.setTimeout(() => {
    setReportToast({ visible: false, message: '', type: '' });
  }, duration);
};

// ✅ Convert mentions array to lookup map with correct @keys
const mentionMap = React.useMemo(() => {
  const map = {};
  mentions.forEach((m) => {
    // Always store @username in lowercase for matching
    const normalizedKey = `@${m.username.toLowerCase()}`;
    map[normalizedKey] = {
      username: m.username,
      type: m.type,
      name: m.name,
    };
  });
//  console.log('🧭 mentionMap in Post.js:', map);
  return map;
}, [mentions]);




// 🔁 Load / normalize original repost target
useEffect(() => {
  if (!isRepost) {
    setOriginalPost(null);
    setOriginalMissing(false);
    return;
  }

  // ✅ Backend-first path: repostOf should now usually already be a full object
  if (repostOfPost && typeof repostOfPost === 'object') {
    if (
      repostOfPost.isDeleted ||
      repostOfPost.isHidden ||
      repostOfPost.originalPostUnavailable
    ) {
      setOriginalPost(null);
      setOriginalMissing(true);
      return;
    }

    setOriginalPost(repostOfPost);
    setOriginalMissing(false);
    return;
  }

  // ✅ If backend explicitly tells us original is unavailable, do not fetch again
  if (!repostOfPost) {
    setOriginalPost(null);
    setOriginalMissing(true);
    return;
  }

  // Fallback only for older payloads that still send repostOf as an id
  const targetId =
    typeof repostOfPost === 'string'
      ? repostOfPost
      : repostOfPost?._id || repostOfPost?.id;

  if (!targetId) {
    setOriginalPost(null);
    setOriginalMissing(true);
    return;
  }

  let cancelled = false;

  (async () => {
    try {
      const { data } = await api.get(`/posts/${targetId}`);

      if (cancelled) return;

      if (data?.isDeleted || data?.isHidden) {
        setOriginalPost(null);
        setOriginalMissing(true);
      } else {
        setOriginalPost(data);
        setOriginalMissing(false);
      }
    } catch (err) {
      if (cancelled) return;

      const status = err?.response?.status;
      if (status === 404 || status === 410) {
        setOriginalPost(null);
        setOriginalMissing(true);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [isRepost, repostOfPost]);


const original = React.useMemo(() => {
  if (!isRepost || !originalPost) return null;
  const src = originalPost;
  const oUser = src.user || {};

  const originalIsAdmin = oUser?.isAdmin === true || !!oUser?.adminRole;
  const originalAdminRole = oUser?.adminRole || null;
  const originalIsApp =
    src?.isApp === true ||
    src?.accountType === 'app' ||
    oUser?.isApp === true ||
    oUser?.accountType === 'app';


  const origBadges =
    (Array.isArray(src.badgesBrief) && src.badgesBrief.length && src.badgesBrief) ||
    (Array.isArray(oUser.badgesBrief) && oUser.badgesBrief.length && oUser.badgesBrief) ||
    [];


  // 🔹 Original author role
  const merchantActiveOriginal =
    oUser?.merchantActive === true ||
    src.merchantActive === true ||
    src.isMerchant === true ||
    oUser?.isMerchant === true;

  const role = originalIsAdmin
    ? 'Admin'
    : originalIsApp
    ? 'App'
    : merchantActiveOriginal
    ? 'Merchant'
    : 'Pioneer';

const authorIsAdmin = user?.isAdmin === true || !!user?.adminRole;
const authorAdminRole = user?.adminRole || null;


const originalBackgroundTheme =
  src?.style?.backgroundTheme ||
  src?.backgroundTheme ||
  'normal';

const safeOriginalBackgroundTheme = isValidPostStyleTheme(originalBackgroundTheme)
  ? originalBackgroundTheme
  : 'normal';

return {
  id: src.id || src._id,
  username: src.username || oUser.username,
  name: src.name || oUser.name,
  profilePic: src.profilePic || oUser.profilePic,
  badgesBrief: origBadges.slice(0, 3),
  isAdmin: originalIsAdmin,
  adminRole: originalAdminRole,
  isApp: originalIsApp,
  timestamp: src.createdAt || src.timestamp,
  content: src.content || '',
  media: Array.isArray(src.media) ? src.media : (src.media ? [src.media] : []),
  role,

  // ✅ Original post background theme support for repost card
  style: src.style || null,
  backgroundTheme: safeOriginalBackgroundTheme,

hasPaidStyle:
  src.hasPaidStyle === true ||
  src?.style?.paid === true ||
  safeOriginalBackgroundTheme !== 'normal',

// ✅ Action belongs to the original post
postAction: src.postAction || null,

};


}, [isRepost, originalPost]);


const originalPostStyleVars = React.useMemo(() => {
  if (!original?.backgroundTheme) return undefined;

  return getPostStyleCssVars(original.backgroundTheme);
}, [original?.backgroundTheme]);



useEffect(() => {
  const video = videoRef.current;
  if (!video) return;

  if (!isActive) {
    video.pause();
    return;
  }

  if (typeof IntersectionObserver === 'undefined') return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (
        !entry.isIntersecting ||
        entry.intersectionRatio < 0.25
      ) {
        video.pause();
      }
    },
    { threshold: [0, 0.25] }
  );

  observer.observe(video);

  return () => {
    observer.disconnect();
    video.pause();
  };
}, [isActive]);


  const menuRef = useRef(null);
  const popupRef = useRef(null);
  const touchStartX = useRef(null);
  const [likes, setLikes] = useState([]);  // For popup
  const [liked, setLiked] = useState(userHasLiked || false);
  const [saved, setSaved] = useState(false);
  const [shareFeedback, setShareFeedback] = useState('');
  const [comments, setComments] = useState([]);  // For popup
  const [newComment, setNewComment] = useState('');
  const [showLikesPopup, setShowLikesPopup] = useState(false);
  const [showCommentsPopup, setShowCommentsPopup] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewVideoRef = useRef(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const carouselRef = useRef(null);
  const [carouselIoRef, carouselInView] = useInViewport({ rootMargin: '400px' });
  const [showTxnPopup, setShowTxnPopup] = useState(false);
  const [showTipPopup, setShowTipPopup] = useState(false);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [showTipListDrawer, setShowTipListDrawer] = useState(false);
  const { toggleSavePost, isSaved } = useSavedPosts();
  const [tipList, setTipList] = useState([]);
  const [tipCount, setTipCount] = useState(0);
  const [cmtDelta, setCmtDelta] = useState(0);



// inside Post.js: replace or ensure your GridImage component matches this
function GridImage({ url, alt, onClick, width, height }) {
  const [imgRef, inView] = useInViewport({ rootMargin: '1000px' });

  return (
    <div ref={imgRef} style={{ width: '100%', height: '100%' }}>
      <PostImage
        src={url}
        alt={alt}
        className="carousel-img"
        shouldLoad={inView}     // defer actual src until near viewport
        priority={inView}       // make browser treat nearest ones as high priority
        onClick={onClick}
        loading="lazy"
        width={width}
        height={height}
      />
    </div>
  );
}



useEffect(() => {
  const fetchTipCount = async () => {
    try {
      const res = await api.get(`/tips/count/${id}`);
      setTipCount(res.data.count || 0);
    } catch (err) {
      console.error('❌ Failed to fetch tip count:', err);
    }
  };
  fetchTipCount();
}, [id]);

  useEffect(() => {
  const el = carouselRef.current;
  if (!el) return;

  const handleScroll = () => {
    const scrollLeft = el.scrollLeft;
    const childWidth = el.firstChild?.offsetWidth + 10; // 10 = image gap
    const index = Math.round(scrollLeft / childWidth);
    setCurrentImageIndex(index);
  };
  el.addEventListener('scroll', handleScroll);
  return () => el.removeEventListener('scroll', handleScroll);
}, []);

const [isFollowing, setIsFollowing] = useState(false); // I follow them?
const [followsMe, setFollowsMe] = useState(false);     // they follow me?


// 🔒 Privacy/block state for post author
const [isBlocked, setIsBlocked] = useState(false);
const [blockActionLoading, setBlockActionLoading] = useState(false);
const [showBlockConfirmDrawer, setShowBlockConfirmDrawer] = useState(false);


// =========================================================
// POST OVERLAY STATE
// One source of truth for whether this Post currently has
// any fullscreen popup / drawer / modal open.
// =========================================================

const anyPostOverlayOpen =
  previewOpen ||
  showLikesPopup ||
  showCommentsPopup ||
  showTipPopup ||
  showTipListDrawer ||
  showShareDrawer ||
  showActionDrawer ||
  showDeleteConfirm ||
  showTxnPopup ||
  showReportPopup ||
  showStakeDrawer ||
  showBlockConfirmDrawer ||
  showQuickTopUp;



useLayoutEffect(() => {
  if (!isActive || !anyPostOverlayOpen) return;

  const owner = {};
  postOverlayOwners.add(owner);

  document.body.classList.add('bf-post-overlay-open');

  return () => {
    postOverlayOwners.delete(owner);

    if (postOverlayOwners.size === 0) {
      document.body.classList.remove('bf-post-overlay-open');
    }
  };
}, [isActive, anyPostOverlayOpen]);


const isVerified = transactionId?.startsWith('pi');



// ✅ Use *current* backend-computed status, not post snapshot
const merchantActiveNow = user?.merchantActive === true;

const authorIsAdmin = user?.isAdmin === true || !!user?.adminRole;
const authorAdminRole = user?.adminRole || null;

const authorIsApp =
  isApp === true ||
  accountType === 'app' ||
  user?.isApp === true ||
  user?.accountType === 'app';

// ✅ UI role priority: Admin -> App -> Merchant -> Pioneer
const role = authorIsAdmin
  ? 'Admin'
  : authorIsApp
  ? 'App'
  : merchantActiveNow
  ? 'Merchant'
  : 'Pioneer';


const isOwner = currentUser?.toLowerCase() === username?.toLowerCase();

useEffect(() => {
  setSaved(isSaved?.(id) ?? false);
}, [id, isSaved]);


useEffect(() => {
  if (!isActive || !showMenu) return;

  const handleOutside = (event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target)
    ) {
      setShowMenu(false);
    }
  };

  document.addEventListener('mousedown', handleOutside);
  document.addEventListener('touchstart', handleOutside);

  return () => {
    document.removeEventListener('mousedown', handleOutside);
    document.removeEventListener('touchstart', handleOutside);
  };
}, [isActive, showMenu]);

// 🔒 Load privacy/block status only when the action drawer opens.
// This avoids making a privacy request for every post in the feed.

useEffect(() => {
  if (!isActive || !showActionDrawer) return;
  if (!currentUser || !username) return;
  if (String(currentUser).toLowerCase() === String(username).toLowerCase()) return;

  let cancelled = false;

  const loadPrivacyStatus = async () => {
    try {
      const res = await api.get(`/privacy/status/${username}`);

      if (cancelled) return;

      setIsBlocked(Boolean(res.data?.iBlockedThem));
    } catch (err) {
      console.error('❌ Failed to load privacy status:', err);
    }
  };

  loadPrivacyStatus();

  return () => {
    cancelled = true;
  };

}, [isActive, showActionDrawer, currentUser, username]);



  useEffect(() => {
    if (showMenu) setShowMenu(false);
  }, [closeSignal]);


useEffect(() => {
  // 1. Load from localStorage for instant UI update
  const stored = localStorage.getItem(`${currentUser}-isFollowing-${username}`);
  if (stored !== null) {
    setIsFollowing(JSON.parse(stored));
  }
  // 2. Fetch live follow state from backend
  const checkFollowing = async () => {
    if (!currentUser || !username || currentUser === username || username === '') return;
  try {
  const res = await api.get(`/follow/follow-stats/${currentUser}/${username}`);
  const viewerFollowing = Array.isArray(res.data?.viewerFollowing) ? res.data.viewerFollowing : [];
  const targetFollowing = Array.isArray(res.data?.targetFollowing) ? res.data.targetFollowing : [];
  // normalize to lowercase to avoid case mismatches
  const me = (currentUser || '').toLowerCase();
  const them = (username || '').toLowerCase();
  const viewerFollowingLC = viewerFollowing.map(u => (u || '').toLowerCase());
  const targetFollowingLC = targetFollowing.map(u => (u || '').toLowerCase());
  // I follow them? (viewer -> target)
  setIsFollowing(viewerFollowingLC.includes(them));
  // They follow me? (target -> viewer)  ✅ this is what powers "Follow back"
  setFollowsMe(targetFollowingLC.includes(me));
} catch (err) {
  console.error('❌ Failed to fetch follow state:', err);
}
  };

  checkFollowing();
}, [currentUser, username, location.key]);

const { getPostInteractions } = usePostContext();

// --- viewer-only localStorage overrides (kept per post, per user)
const likeLSKey = React.useMemo(
  () => `bf_like_${id}_${currentUser}`,
  [id, currentUser]
);

// If we have a local override for "liked by me", apply it on mount/prop change
useEffect(() => {
  try {
    const raw = localStorage.getItem(likeLSKey);
    if (raw !== null) {
      const override = JSON.parse(raw); // true/false
      setLiked(Boolean(override));
    } else {
      // fall back to server-prop when no override saved yet
      setLiked(Boolean(userHasLiked));
    }
  } catch {
    setLiked(Boolean(userHasLiked));
  }
}, [userHasLiked, likeLSKey]);


useEffect(() => {
  if (!isActive || !previewOpen) return;


  const onPopState = (e) => {
    if (openingStudioRef.current) {
      openingStudioRef.current = false;
      pushedFsHistoryRef.current = false;
      closingFromButtonRef.current = false;

      setPreviewOpen(false);
      setFsAnimating(false);
      setFsNextIndex(null);
      setFsSwipeDir(0);

      navigate('/studio/avatar', {
        state: {
          studioEntryPoint: 'post_fullscreen',
        },
      });
      return;
    }

    // If fullscreen is open and the back gesture happens, just close the overlay 

   if (previewOpen) {
      // If this was triggered by our own back-button handler, just reset the flag and ignore
      if (closingFromButtonRef.current) {
        closingFromButtonRef.current = false;
        pushedFsHistoryRef.current = false; // we've consumed the dummy entry
        return;
      }

      try { previewVideoRef.current?.pause(); } catch {}
      setPreviewOpen(false);
      setFsAnimating(false);
      setFsNextIndex(null);
      setFsSwipeDir(0);

      // We just landed on the previous history entry (same URL). Clear our flag.
      pushedFsHistoryRef.current = false;

      // Prevent any further unintended navigation (optional, keeps stack stable):
      // push the current state back so a second back actually leaves the page.
      try {
        if (!window.history.state || window.history.state.bfFsPreview) {
          window.history.replaceState({}, "");
        }
      } catch {}
    }
  };


window.addEventListener('popstate', onPopState);

return () => {
  window.removeEventListener('popstate', onPopState);
};

}, [isActive, previewOpen, navigate]);





useEffect(() => {
  return () => {
    if (pushedFsHistoryRef.current && window.history.state?.bfFsPreview) {
      // pop our dummy entry so user history isn't polluted
      try { window.history.back(); } catch {}
      pushedFsHistoryRef.current = false;
    }
  };
}, []);

// 🔁 Local stake UI (optimistic)
const [stakeUi, setStakeUi] = useState({
  activePi: Number(stakeActivePi || 0),
  nextExpiry: stakeNextExpiryAt || null,
});

// Keep in sync if parent/props change (e.g., refetch)
useEffect(() => {
  setStakeUi({
    activePi: Number(stakeActivePi || 0),
    nextExpiry: stakeNextExpiryAt || null,
  });
}, [stakeActivePi, stakeNextExpiryAt]);




// 🔄 Live stake summary fetch (fills badge + next expiry)
useEffect(() => {
  let stop = false;
  async function load() {
    try {
      const { data } = await getStakeSummary(id, {
        viewer: currentUser,
        network: effectiveNetwork,
      });
      if (stop) return;
      setStakeUi({
        activePi: Number(
          data?.stakeActivePi ??
          data?.totalActiveRefundable ??
          0
        ),
        nextExpiry: data?.stakeNextExpiryAt || null,
      });
    } catch {
      // silent fail ok
    }
  }
  // Only fetch if prop didn’t already provide a non-zero number
  if (!Number(stakeActivePi || 0)) load();
  return () => { stop = true; };
}, [id, currentUser, effectiveNetwork]); // eslint-disable-line



// 🔒 Open block/unblock confirmation drawer
const handleToggleBlock = () => {
  if (!currentUser || !username) {
    showReportToast('You need to be logged in to block users.', 'error');
    return false;
  }

  if (String(currentUser).toLowerCase() === String(username).toLowerCase()) {
    showReportToast('You cannot block yourself.', 'error');
    return false;
  }

  setShowActionDrawer(false);

  // Open the clean confirmation drawer after the action drawer closes
  setTimeout(() => {
    setShowBlockConfirmDrawer(true);
  }, 120);

  return false;
};

const confirmToggleBlock = async () => {
  const nextAction = isBlocked ? 'unblock' : 'block';

  try {
    setBlockActionLoading(true);

    await api.post(`/privacy/${nextAction}`, {
      targetUsername: username,
    });

    setIsBlocked(!isBlocked);
    setShowBlockConfirmDrawer(false);

    showReportToast(
      isBlocked ? `@${username} has been unblocked.` : `@${username} has been blocked.`,
      'success'
    );
  } catch (err) {
    console.error(`❌ Failed to ${nextAction} user:`, err);

    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      `Failed to ${nextAction} user. Please try again.`;

    showReportToast(msg, 'error');
  } finally {
    setBlockActionLoading(false);
  }
};


  // Toggle follow/unfollow action
const toggleFollow = async () => {
 // console.log('🔁 Toggle follow in Post.js:', currentUser, '->', username);
  try {
    await api.post('/follow/toggle', {
      follower: currentUser,
      following: username
    });

    // Re-fetch and sync follow state
    const res = await api.get(`/follow/follow-stats/${currentUser}/${username}`);
    const updated = res.data.viewerFollowing.includes(username);
    setIsFollowing(updated);

    // Sync localStorage to match UserProfile logic
    localStorage.setItem(`${currentUser}-isFollowing-${username}`, updated);

    // Optional notification
    if (updated && currentUser !== username) {
      await api.post('/notify', {
        type: 'follow',
        fromUser: currentUser,
        toUser: username,
        postId: null,
        text: `${currentUser} started following you`
      });
    }
  } catch (err) {
    console.error('❌ Post.js follow toggle error:', err);
  } finally {
    setShowMenu(false);
  }
};




// 🔝 local pin state (sync to prop)
  const [isPinned, setIsPinned] = useState(Boolean(pinned));
  useEffect(() => { setIsPinned(Boolean(pinned)); }, [pinned]);

  // 🔝 toggle pin/unpin (admin-only on backend; others will get 403)
  const handlePinToggle = async () => {
    try {
      const route = isPinned ? `/posts/${id}/unpin` : `/posts/${id}/pin`;
      await api.post(route); // api is already configured with auth headers
      setIsPinned(!isPinned);
      showReportToast(isPinned ? 'Unpinned from top.' : 'Pinned to top.', 'success'); // reuse toast
    } catch (err) {
      console.error('❌ Pin toggle failed:', err);
      const status = err?.response?.status;
      if (status === 403) {
        showReportToast('Admin only: you cannot pin.', 'error');
      } else {
        showReportToast('Failed to toggle pin. Try again.', 'error');
      }
      }
  };




const handleCloseCommentsPopup = useCallback(() => {
  setShowCommentsPopup(false);
}, []);



const handleCloseLikesPopup = useCallback(() => {
  setShowLikesPopup(false);
}, []);

const handleCloseTipPopup = useCallback(() => {
  setShowTipPopup(false);
}, []);

const handleCloseTipListDrawer = useCallback(() => {
  setShowTipListDrawer(false);
}, []);

const showTimedTipBanner = useCallback((message, type = 'error', duration = 2000) => {
  if (tipBannerTimerRef.current) {
    clearTimeout(tipBannerTimerRef.current);
  }

  setTipMessage(message);
  setTipType(type);

  tipBannerTimerRef.current = setTimeout(() => {
    setTipMessage('');
    setTipType('');
  }, duration);
}, []);


useEffect(() => {
  return () => {
    if (quickTopUpTimerRef.current) {
      clearTimeout(quickTopUpTimerRef.current);
    }
    if (tipBannerTimerRef.current) {
      clearTimeout(tipBannerTimerRef.current);
    }
  };
}, []);

useEffect(() => {
  if (!isActive || !autoOpenTipDrawer) return;
  if (autoTipOpenedRef.current) return;
  if (!currentUser || !id) return;

  // optional safety: only auto-open if this notification was about this post owner
  if (
    autoOpenTipTargetUsername &&
    String(autoOpenTipTargetUsername).toLowerCase() !== String(username || '').toLowerCase()
  ) {
    return;
  }

  autoTipOpenedRef.current = true;
  setShowTipPopup(true);

}, [
  isActive,
  autoOpenTipDrawer,
  autoOpenTipTargetUsername,
  currentUser,
  id,
  username,
]);


const handleLike = async () => {
  // Burst animation (unchanged)
  const likeBtn = document.querySelector(`#post-${id} .like-btn`);
  if (likeBtn) {
    likeBtn.classList.remove('burst');
    void likeBtn.offsetWidth;
    likeBtn.classList.add('burst');
  }

  // Optimistically flip UI + LS immediately
  const nextLiked = !liked;
  setLiked(nextLiked);
  try {
    localStorage.setItem(likeLSKey, JSON.stringify(nextLiked));
  } catch {}

  try {
    // Hit backend
    await api.post(`/posts/${id}/like`, {
      username: currentUser,
      name: name || user.name || ''
    });

    // Refresh popup data (likes list) but do NOT block UI
    try {
      const data = await getPostInteractions(id, true);
      setLikes(data.likes || []);
      // If backend says otherwise, keep LS override for Home screen until it catches up
      // but sync this Post component's heart with our optimistic state
    } catch {}

    // Notify owner (unchanged)
    if (currentUser !== username && nextLiked) {
      await api.post('/notify', {
        type: 'like',
        fromUser: currentUser,
        toUser: username,
        postId: id,
        text: `${currentUser} liked your post`
      }).catch(() => {});
    }
  } catch (err) {
    console.error('❌ Like failed:', err);
    // Roll back UI + LS if request truly failed
    setLiked(!nextLiked);
    try {
      localStorage.setItem(likeLSKey, JSON.stringify(!nextLiked));
    } catch {}
  }
};




const handleComment = async () => {
  if (!newComment?.trim()) return;

  const isReply = !!replyingTo;
  const text = newComment.trim();

  try {
    const endpoint = isReply
      ? `/posts/${id}/reply-comment`
      : `/posts/${id}/comment`;

    // 1) Call your backend to CREATE the comment/reply
    const res = await api.post(endpoint, {
      postId: id,
      commentId: replyingTo,    // parent comment id when replying
      username: currentUser,
      text
    });

    // 2) Pull the created entity from the response (be flexible about shape)
    const data = res.data || {};
    const created =
      data.comment || data.reply || data.created || data.item || data;

    // 3) Normalize to a stable shape with the REAL id from server
    const createdNorm = {
      ...created,
      id: created?.id || created?._id || created?.commentId || created?.replyId,
      username: created?.username || created?.user?.username || currentUser,
      text: created?.text ?? text,
      createdAt: created?.createdAt || created?.timestamp || created?.date || new Date().toISOString(),
      profilePic: created?.profilePic,
      replies: Array.isArray(created?.replies) ? created.replies : [],
    };

    if (!createdNorm.id) {
      // If your backend returns the *whole updated list* instead of a single item,
      // adapt here to use that list. As a last resort, throw so we don't insert a temp id.
      throw new Error('Server did not return a real comment id');
    }

    // 4) Ensure profile picture (if backend didn't return it)
    if (!createdNorm.profilePic) {
      try {
        const ures = await api.get(`/users/${createdNorm.username}`);
        createdNorm.profilePic = ures.data?.profilePic || '';
      } catch {
        createdNorm.profilePic = '';
      }
    }

    // 5) Update local UI with the **server-created** entity (no temp ids)
    if (isReply) {
      setComments(prev =>
        prev.map(c => {
          const cid = c.id || c._id || c.commentId;
          if (cid !== replyingTo) return c;
          const newReply = {
            id: createdNorm.id,
            username: createdNorm.username,
            text: createdNorm.text,
            createdAt: createdNorm.createdAt,
            profilePic: createdNorm.profilePic,
          };
          return { ...c, replies: [newReply, ...(c.replies || [])] };
        })
      );
    } else {
      setComments(prev => [createdNorm, ...prev]);
    }

    // 6) Clear composer state
    setNewComment('');
    setReplyingTo(null);

// 6.5) Instant UI bump (viewer-only, no flicker)
 if (!isReply) {
   setCmtDelta((d) => d + 1);
 }

    // 7) Fire notification (optional)
    if (currentUser !== username) {
      await api.post('/notify', {
        type: isReply ? 'reply' : 'comment',
        fromUser: currentUser,
        toUser: username,   // post owner username prop
        postId: id,
        text: isReply
          ? `${currentUser} replied to a comment on your post`
          : `${currentUser} commented on your post`,
      });
    }


    // 7.1) If this was a reply, also notify the comment owner (if different)
    if (isReply) {
      // Find the parent comment in current state
      const parent = comments.find(
        (c) => (c.id || c._id || c.commentId) === replyingTo
      );

      const commentOwner = parent?.username;

      if (
        commentOwner &&
        commentOwner !== currentUser && // don't notify yourself
        commentOwner !== username       // avoid double-notifying post owner
      ) {
        await api.post('/notify', {
          type: 'reply_comment',             // 👈 new type
          fromUser: currentUser,
          toUser: commentOwner,
          postId: id,
          text: `${currentUser} replied to your comment`,
        });
      }
    }



    // 8) ✅ Return the created comment so CommentDrawer can use the REAL id immediately
    return createdNorm;

  } catch (err) {
    console.error('❌ Comment failed:', err);
    // optional: show toast
  }
};




  const handleLikeComment = async (commentId) => {
    try {
      await api.post('/posts/like-comment', {
        postId: id,
        commentId,
        username: currentUser
      });

const data = await getPostInteractions(id, true);
setComments(data.comments || []);

    } catch (err) {
      console.error('❌ Like comment failed:', err);
    }
  };

const handleSavePost = async () => {
  toggleSavePost(id, !saved); // only pass ID now
  setSaved(!saved); // toggle UI state

  try {
    await api.post(`/interactions/save/${id}`);

//    console.log(`💾 Save tracked for post: ${id}`);
  } catch (err) {
    console.error('❌ Failed to track save:', err);
  }
};




const handleShare = async () => {
  try {
    const postLink = `https://barterfi.app/post/${id}`;
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(postLink);
      setShareFeedback('Copied!');
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = postLink;
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareFeedback('Copied!');
    }

    try {
      await api.post(`/interactions/share/${id}`);
  
    } catch (err) {
      console.error('❌ Failed to track share:', err);
    }

    setTimeout(() => setShareFeedback(''), 2000);
  } catch (err) {
    console.error('Share failed:', err);
    setShareFeedback('Failed to copy');
    setTimeout(() => setShareFeedback(''), 2000);
  }
};

const confirmDelete = async () => {
  setShowDeleteConfirm(false);
  try {
    const res = await api.delete(`/posts/${id}`);
    if (res.status === 200 && onDelete) onDelete(id); // ✅ Pass ID here
  } catch (err) {
    console.error('❌ Delete failed:', err);
  }
};



const getTimeAgo = (ts) => {
  if (!ts) return '';

  // Accept ISO string, ms or seconds (len 10) – normalize to ms.
  let t = ts;
  if (typeof t === 'number' && String(t).length === 10) t = t * 1000; // old UNIX seconds
  const postTime = new Date(t);
  if (isNaN(postTime.getTime())) return '';

  const now = new Date();
  let diffMs = now.getTime() - postTime.getTime();
  if (diffMs < 0) diffMs = 0; // future-proof

  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;

  const day = Math.floor(hr / 24);
  if (day <= 7) return `${day}d ago`;

  // From day 8 up to < 1 year: "D mon" (e.g., "23 aug")
  if (day < 365) {
    const d = postTime.getDate();
    const mon = postTime.toLocaleString('en-US', { month: 'short' }).toLowerCase();
    return `${d} ${mon}`;
  }


// >= 1 year: "D mon YY" (e.g., "30 jun 25")
const d = postTime.getDate();
const mon = postTime
  .toLocaleString('en-US', { month: 'short' })
  .toLowerCase();
const year = String(postTime.getFullYear()).slice(-2);

return `${d} ${mon} ${year}`;

};

const handleSwipeStart = (e) => {
  touchStartX.current = e.touches[0].clientX;
};

const handleSwipeEnd = (e) => {
  if (!touchStartX.current) return;
  const deltaX = e.changedTouches[0].clientX - touchStartX.current;
  const threshold = 50;
  const total = imageItems.length || 1;
  if (deltaX > threshold) {
    setCurrentImageIndex(prev => (prev === 0 ? total - 1 : prev - 1));
  } else if (deltaX < -threshold) {
    setCurrentImageIndex(prev => (prev + 1) % total);
  }
  touchStartX.current = null;
};


const fallbackPic = profilePic?.startsWith('http')
  ? profilePic
  : user?.profilePic?.startsWith('http')
    ? user.profilePic
    : require('./assets/placeholder-profile.png');

const displayName = name || user?.name || username;
const createdAtDate = new Date(timestamp || Date.now());
const ageInMinutes = (Date.now() - createdAtDate.getTime()) / 60000;

const isFresh = ageInMinutes <= 12 * 60;
const showNewBadge = isFresh && badgesBrief.length === 0;



// --- Edited flag (ONLY when the post was actually edited) ---
const isEdited = React.useMemo(() => {
  return Boolean(editedAt) || Number(editCount || 0) > 0;
}, [editedAt, editCount]);




const rawList = Array.isArray(media) ? media : (media ? [media] : []);
 const mediaItems = rawList
   .map(item => {
     // Back-compat: server might return strings for old posts
     if (typeof item === 'string') {
       return { url: item, type: classifyMediaUrl(item) };
     }
     // New shape: { url, width, height, [type] }
     if (item && typeof item === 'object' && item.url) {


       return {
         url: item.url,
         width: item.width,
         height: item.height,
         type: classifyMediaUrl(item.url),
         source:
           item.source === 'studio_avatar'
             ? 'studio_avatar'
             : undefined,
       };

     }
     return null;
   })
   .filter(Boolean);

const imageItems = mediaItems.filter(m => m.type === 'image');
const hasOnlyImages = imageItems.length === mediaItems.length && imageItems.length > 0;

// indices of only the images inside mediaItems (for fullscreen)
const imageIndices = React.useMemo(
  () => mediaItems.map((m, idx) => (m.type === 'image' ? idx : null)).filter(v => v !== null),
  [mediaItems]
);

const isPreviewingImage =
  mediaItems[previewIndex]?.type === 'image';

// currentUser is the logged-in username in this component.
const canCreateStudioAvatar = canAccessStudio({
  username: currentUser,
});

const showAvatarStudioCta =
  isPreviewingImage &&
  mediaItems[previewIndex]?.source === 'studio_avatar' &&
  canCreateStudioAvatar;



function handleCreateStudioAvatar() {
  if (!showAvatarStudioCta || openingStudioRef.current) return;

  // Guard both navigation paths against repeated taps.
  openingStudioRef.current = true;

  void trackStudioEvent(
    createStudioAnalyticsEvent(
      'studio_entry_clicked',
      { entryPoint: 'post_fullscreen' }
    )
  );

  try {
    previewVideoRef.current?.pause();
  } catch {}

  // Consume the fullscreen history entry before entering Studio.
  // onPopState carries the same entry point into Studio.
  if (
    pushedFsHistoryRef.current &&
    window.history.state?.bfFsPreview
  ) {
    window.history.back();
    return;
  }

  pushedFsHistoryRef.current = false;
  closingFromButtonRef.current = false;
  setPreviewOpen(false);
  setFsAnimating(false);
  setFsNextIndex(null);
  setFsSwipeDir(0);

  navigate('/studio/avatar', {
    state: {
      studioEntryPoint: 'post_fullscreen',
    },
  });

  openingStudioRef.current = false;
}



// --- Fullscreen image slide animation ---
const [fsAnimating, setFsAnimating] = useState(false);
const [fsNextIndex, setFsNextIndex] = useState(null);
const [fsSwipeDir, setFsSwipeDir] = useState(0); // +1 = next, -1 = prev

useLayoutEffect(() => {
  if (isActive) return;

  videoRef.current?.pause();
  previewVideoRef.current?.pause();

  // Also cover media rendered inside this post's child components.
  postViewRef.current
    ?.querySelectorAll('video, audio')
    .forEach((mediaElement) => {
      mediaElement.pause();
    });

  setPreviewOpen(false);
  setShowLikesPopup(false);
  setShowCommentsPopup(false);
  setShowMenu(false);
  setShowDeleteConfirm(false);
  setShowTxnPopup(false);
  setShowTipPopup(false);
  setShowReportPopup(false);
  setShowTipListDrawer(false);
  setShowShareDrawer(false);
  setShowActionDrawer(false);
  setShowStakeDrawer(false);
  setShowBlockConfirmDrawer(false);
  setShowQuickTopUp(false);

  setFsAnimating(false);
  setFsNextIndex(null);
  setFsSwipeDir(0);

  if (quickTopUpTimerRef.current) {
    clearTimeout(quickTopUpTimerRef.current);
    quickTopUpTimerRef.current = null;
  }

  if (tipBannerTimerRef.current) {
    clearTimeout(tipBannerTimerRef.current);
    tipBannerTimerRef.current = null;
  }

  setTipMessage('');
  setTipType('');
  setReportToast({ visible: false, message: '', type: '' });

  // Do not navigate backwards when deactivating this post.
  pushedFsHistoryRef.current = false;
  closingFromButtonRef.current = false;
}, [isActive, postViewRef]);

// ⛔ NO WRAP: clamp at first/last image
const animateFsImage = (dir /* -1 prev, +1 next */) => {
  if (!isPreviewingImage || imageIndices.length <= 1) return;
  if (fsAnimating) return;

  const pos = imageIndices.indexOf(previewIndex);
  if (pos === -1) return;

  // block going left at the last image, and right at the first image
  if (dir === +1 && pos === imageIndices.length - 1) return; // already at last → no-op
  if (dir === -1 && pos === 0) return;                        // already at first → no-op

  const nextPos = pos + dir;              // <- no modulo
  const nextIdx = imageIndices[nextPos];  // valid because we clamped above

  setFsSwipeDir(dir);
  setFsNextIndex(nextIdx);
  setFsAnimating(true);
};

// touch for fullscreen images
const fsTouchStartX = useRef(null);
const handleFSTouchStart = (e) => { fsTouchStartX.current = e.touches?.[0]?.clientX ?? null; };
const handleFSTouchEnd = (e) => {
  if (!isPreviewingImage || imageIndices.length <= 1) return;
  if (fsTouchStartX.current == null) return;
  const endX = e.changedTouches?.[0]?.clientX ?? fsTouchStartX.current;
  const dx = endX - fsTouchStartX.current;
  const threshold = 40;
  if (dx > threshold) animateFsImage(-1);     // swipe right → previous
  else if (dx < -threshold) animateFsImage(+1); // swipe left → next
  fsTouchStartX.current = null;
};


// keyboard arrows in fullscreen (use animation)
useEffect(() => {
  if (!isActive || !previewOpen) return;
  const onKey = (e) => {
    if (e.key === 'Escape') {
      try { previewVideoRef.current?.pause(); } catch {}
      setPreviewOpen(false);
      return;
    }
    if (!isPreviewingImage || imageIndices.length <= 1) return;

    const pos = imageIndices.indexOf(previewIndex);
    if (e.key === 'ArrowLeft') {
      if (pos > 0) animateFsImage(-1);       // ⛔ stop at first
    }
    if (e.key === 'ArrowRight') {
      if (pos < imageIndices.length - 1) animateFsImage(+1); // ⛔ stop at last
    }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);

}, [
  isActive,
  previewOpen,
  isPreviewingImage,
  imageIndices,
  previewIndex,
  fsAnimating,
]);



// Play fullscreen video only while this Post is active.
useEffect(() => {
  if (!isActive || !previewOpen) return;

  const current = mediaItems[previewIndex];
  if (current?.type !== 'video') return;

  let cancelled = false;
  let playingVideo = null;

  const frameId = requestAnimationFrame(() => {
    if (cancelled) return;

    const video = previewVideoRef.current;
    if (!video) return;

    playingVideo = video;

    const playVideo = async () => {
      try {
        await video.play();
      } catch {
        if (cancelled) return;

        video.muted = true;

        try {
          await video.play();
        } catch {
          // User can start playback manually if autoplay is blocked.
        }
      }

      if (cancelled) {
        video.pause();
      }
    };

    playVideo();
  });

  return () => {
    cancelled = true;
    cancelAnimationFrame(frameId);
    playingVideo?.pause();
  };

  // mediaItems is recreated each render; index selects the preview.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isActive, previewOpen, previewIndex]);




useLayoutEffect(() => {
  if (!isActive || !previewOpen) {
    setFsAnimating(false);
    setFsNextIndex(null);
    setFsSwipeDir(0);
    return;
  }

  const owner = {};
  fullscreenPreviewOwners.add(owner);

  document.body.classList.add('bf-fullscreen-open');

  return () => {
    fullscreenPreviewOwners.delete(owner);

    if (fullscreenPreviewOwners.size === 0) {
      document.body.classList.remove('bf-fullscreen-open');
    }
  };
}, [isActive, previewOpen]);






const [displayedViewCount, setDisplayedViewCount] = useState(() =>
  Math.max(0, Number(viewCount) || 0)
);

useEffect(() => {
  setDisplayedViewCount(Math.max(0, Number(viewCount) || 0));
}, [viewCount]);

useEffect(() => {
  const handleViewCountUpdate = (event) => {
    const updatedPostId = String(event?.detail?.postId || '');
    const updatedViewCount = Number(event?.detail?.viewCount);

    if (
      updatedPostId !== String(id) ||
      !Number.isFinite(updatedViewCount)
    ) {
      return;
    }

    setDisplayedViewCount(Math.max(0, updatedViewCount));
  };

  window.addEventListener(
    POST_VIEW_COUNT_UPDATED_EVENT,
    handleViewCountUpdate
  );

  return () => {
    window.removeEventListener(
      POST_VIEW_COUNT_UPDATED_EVENT,
      handleViewCountUpdate
    );
  };
}, [id]);




const displayedCommentsCount = showCommentsPopup
  ? Math.max(comments.length || 0, (commentsCount || 0) + cmtDelta)
  : (commentsCount || 0) + cmtDelta;



return !id ? null : (
  <>



<div
  ref={postViewRef}
  className={[
    'post',
    `post-screen-${currentScreen}`,
    `post-theme-${safeBackgroundTheme}`,
    hasStyledBackground ? 'post-has-background-theme' : '',
  ]
    .filter(Boolean)
    .join(' ')}
  id={wrapperId || `post-${id}`}
  style={postStyleVars}
>


<div className="post-menu">
  <button className="menu-icon" onClick={() => setShowActionDrawer(true)}>⋮</button>
</div>


{Number(stakeUi.activePi) > 0 && (
  <div className="post-boosted-row">
    <span
      className={`post-boosted-pill ${
        onStakeDetailsClick ? 'is-clickable' : ''
      }`}
      title={
        stakeUi.nextExpiry
          ? `Boosted • ${Number(stakeUi.activePi).toFixed(2)} Pi • next expiry ${new Date(
              stakeUi.nextExpiry
            ).toLocaleString()}`
          : `Boosted • ${Number(stakeUi.activePi).toFixed(2)} Pi`
      }
      onClick={(e) => {
        e.stopPropagation();
        if (onStakeDetailsClick) onStakeDetailsClick();
      }}
      role={onStakeDetailsClick ? 'button' : undefined}
      tabIndex={onStakeDetailsClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (
          onStakeDetailsClick &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          e.stopPropagation();
          onStakeDetailsClick();
        }
      }}
    >
      <span>BOOSTED</span>
      <img src={stakeBadgeIcon} alt="" aria-hidden="true" />
    </span>
  </div>
)}

{!isPinned && !(Number(stakeUi.activePi) > 0) && showNewBadge && (
  <div className="post-new-row">
    <span className="new-badge">NEW</span>
  </div>
)}



<div className="user-info">


<img
  src={fallbackPic}
  alt="avatar"
  className={`avatar ${authorIsAdmin || authorIsApp ? 'avatar-admin' : ''}`}
  title={
    authorIsAdmin
      ? (authorAdminRole || 'admin')
      : authorIsApp
      ? 'app account'
      : ''
  }
  onClick={() => { saveHomeScrollPos(); navigate(`/user/${username}`); }}
  style={{ cursor: 'pointer' }}
/>



<div className="post-author-meta">
  {/* Row 1: Full name • time (+ NEW if fresh) */}
  <div
    className="name-line"

   onClick={() => { saveHomeScrollPos(); navigate(`/user/${username}`); }}
    style={{ cursor: 'pointer' }}
  >
  

<span className="name-with-admin">
  <strong className="name-ellipsis">{displayName}</strong>


{(authorIsAdmin || authorIsApp) && (
  <img
    src={goldTickIcon}
    alt={authorIsAdmin ? 'Admin verified' : 'App verified'}
    className="admin-gold-tick"
    title={authorIsAdmin ? (authorAdminRole || 'Admin') : 'App account'}
  />
)}


</span>
 
{badgesBrief.length > 0 && (
      <span className="post-badges" aria-label="Badges">
        {badgesBrief.map((bid) => <BadgeGlyph key={bid} id={bid} />)}
      </span>
    )}


<span className="dot-sep" aria-hidden="true">•</span>
<span className="timestamp-inline">{getTimeAgo(timestamp)}</span>



{isPinned && (
  <img src={pinIcon} alt="Pinned" className="pin-badge" />
)}


{transactionId && (
  <img
    src={p2pIcon}
    alt="Verified P2P payment"
    title="Verified P2P payment"
    className="p2p-badge"
    onClick={(e) => {
      e.stopPropagation();      
      setShowTxnPopup(true);   
    }}
  />
)}




  </div>

  {/* Row 2: Role */}


  <div className="role-row">
    {role === 'Pioneer' ? (
      <span className="pioneer-pill">Pioneer</span>
    ) : (
      <span className="merchant-pill">{role}</span>
    )}
  </div>

</div>




</div>



  {/* ✅ INDENTED POST CONTENT STARTS HERE */}
  <div className="post-body-indented">

    <div className="post-text">

<ReadMoreText
  text={mainText}
  lineClampMobile={5}
  lineClampDesktop={8}
  parseFn={(text) => parseMentions(text, mentionMap)}
  suffix={isEdited ? <span className="bf-edited-tag"> (edited)</span> : null}
/>

    </div>


{poll && (
  <PostPollBlock
    postId={id}
    poll={poll}
    currentUser={currentUser}
    onPollVoteUpdate={onPollVoteUpdate}
  />
)}

    {/* 🔁 Repost placeholder or embed */}
    {isRepost && (
      <>
        {originalMissing && (
          <div className="repost-card repost-card-missing">
            <div className="repost-card-missing-title">
              This post has been deleted or hidden
            </div>
            <div className="repost-card-missing-sub">
              The original content is no longer available.
            </div>
          </div>
        )}

        {!originalMissing && original && (



<div
  className={`repost-card repost-card-theme-${
    original.backgroundTheme || 'normal'
  } ${
    original.backgroundTheme &&
    original.backgroundTheme !== 'normal'
      ? 'repost-card-has-background-theme'
      : ''
  }`}
  style={originalPostStyleVars}
  role="button"
  tabIndex={0}
  onClick={() => {
    saveHomeScrollPos();

    if (original.id) {
      navigate(`/post/${original.id}`);
    } else if (original.username) {
      navigate(`/user/${original.username}`);
    }
  }}
  onKeyDown={(event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    saveHomeScrollPos();

    if (original.id) {
      navigate(`/post/${original.id}`);
    } else if (original.username) {
      navigate(`/user/${original.username}`);
    }
  }}
>



            <div className="repost-card-header">
              <img
                src={
                  original.profilePic?.startsWith('http')
                    ? original.profilePic
                    : require('./assets/placeholder-profile.png')
                }
                alt={original.name || original.username}
  
className={`repost-card-avatar ${original?.isAdmin || original?.isApp ? 'avatar-admin' : ''}`}

              />

              <div className="repost-card-header-text">
                {/* Row 1: Full name • time (+ badges) */}
                <div className="repost-card-name-row">


<span className="name-with-admin">
  <span className="repost-card-name">
    {original.name || original.username}
  </span>


  {(original?.isAdmin || original?.isApp) && (
    <img
      src={goldTickIcon}
      alt={original?.isAdmin ? 'Admin verified' : 'App verified'}
      className="admin-gold-tick"
      title={original?.isAdmin ? (original?.adminRole || 'Admin') : 'App account'}
    />
  )}

</span>

                  {original.badgesBrief?.length > 0 && (
                    <span className="repost-card-badges">
                      {original.badgesBrief.map((bid) => (
                        <BadgeGlyph key={bid} id={bid} />
                      ))}
                    </span>
                  )}

                  {original.timestamp && (
                    <>
                      <span className="dot-sep" aria-hidden="true">•</span>
                      <span className="repost-card-timestamp">
                        {getTimeAgo(original.timestamp)}
                      </span>
                    </>
                  )}
                </div>

                {/* Row 2: Role pill (Merchant / Pioneer) */}
 

                <div className="repost-card-role-row">
                  {original.role === 'Pioneer' ? (
                    <span className="pioneer-pill">Pioneer</span>
                  ) : (
                    <span className="merchant-pill">{original.role}</span>
                  )}
                </div>


              </div>
            </div>

            {original.content && (
              <div className="repost-card-content">
                <ReadMoreText
                  text={original.content}
                  lineClampMobile={4}
                  lineClampDesktop={6}
                  parseFn={(text) => parseMentions(text, mentionMap)}
                />
              </div>
            )}



{Array.isArray(original.media) && original.media.length > 0 && (
  <div className="repost-card-media">
    {(() => {
      // Normalize original.media to the same shape as main post
      const normalized = original.media
        .map((item) => {
          if (!item) return null;

          if (typeof item === 'string') {
            return {
              url: item,
              type: classifyMediaUrl(item),
            };
          }

          if (typeof item === 'object' && item.url) {
            return {
              url: item.url,
              width: item.width,
              height: item.height,
              type: classifyMediaUrl(item.url),
            };
          }

          return null;
        })
        .filter(Boolean);

      if (!normalized.length) return null;

      const imageItems = normalized.filter(
        (item) => item.type === 'image'
      );

      const videoItems = normalized.filter(
        (item) => item.type === 'video'
      );

      // ✅ If there are images, use the same PostMediaGrid layout
      if (imageItems.length > 0) {
        return (
          <PostMediaGrid
            images={imageItems}
            onOpen={() => {
              saveHomeScrollPos();

              if (original.id) {
                navigate(`/post/${original.id}`);
              } else if (original.username) {
                navigate(`/user/${original.username}`);
              }
            }}
          />
        );
      }

      // 🎥 Only videos
      if (videoItems.length > 0) {
        const firstVideo = videoItems[0];

        return (
          <div className="repost-card-media-video">
            <PostVideo
              src={firstVideo.url}
              poster={thumbnail}
              muted
            />
          </div>
        );
      }

      return null;
    })()}
  </div>
)}

{original.postAction && (
  <PostDestinationAction
    postAction={original.postAction}
    className="is-repost-action"
  />
)}
</div>



        )}
      </>
    )}


    {/* 🛒 Product repost preview */}
    {postType === 'product_repost' && productSnapshot && (
      <ProductRepostPreview productSnapshot={productSnapshot} />
    )}


    {/* 📸 Main post media (outside repost button) */}
    {mediaItems.length > 0 && (
      <>
        {hasOnlyImages ? (
          <PostMediaGrid
            images={imageItems}
            onOpen={(idx) => {
              // translate grid index to your mediaItems index for the fullscreen viewer
              setPreviewIndex(imageIndices[idx]);

              if (!pushedFsHistoryRef.current) {
                try {
                  window.history.pushState({ bfFsPreview: true, postId: id }, "");
                } catch {}
                pushedFsHistoryRef.current = true;
              }
              setPreviewOpen(true);
            }}
          />
        ) : (
          /* Mixed or video-only: keep your existing stack renderer */
          <div className="post-media-grid">
            {mediaItems.map((m, i) =>
              m.type === 'video' ? (
                <PostVideo
                  key={i}
                  src={m.url}
                  poster={thumbnail}
                  onOpen={() => {
                    try { videoRef.current?.pause(); } catch {}
                    setPreviewIndex(i);

                    if (!pushedFsHistoryRef.current) {
                      try {
                        window.history.pushState({ bfFsPreview: true, postId: id }, "");
                      } catch {}
                      pushedFsHistoryRef.current = true;
                    }
                    setPreviewOpen(true);
                  }}
                />
              ) : (


<GridImage
  key={i}
  url={m.url}
  alt={`post-${i}`}
  width={m.width}
  height={m.height}
  onClick={() => {
    setPreviewIndex(i);

    if (!pushedFsHistoryRef.current) {
      try {
        window.history.pushState({ bfFsPreview: true, postId: id }, "");
      } catch {}
      pushedFsHistoryRef.current = true;
    }

    setPreviewOpen(true);
  }}
/>

              )
            )}
          </div>
        )}
      </>
    )}


<PostDestinationAction postAction={postAction} />



<div className="actions">
  {/* Left group: evenly spaced with counts */}
  <div className="actions-left">

    <div className="action-slot">
      <button
        className={`icon-btn like-btn ${liked ? 'liked burst' : ''}`}
        onClick={handleLike}
      >
        <img
          src={liked ? likeIconRed : likeIcon}
          alt="Like"
          className="action-icon"
        />
      </button>
      <span
        className={`action-count ${likes.length > 0 || likesCount > 0 ? 'show-count' : ''}`}

onClick={() => {
  setShowLikesPopup(true);
}}
      >
        {likes.length || likesCount}
      </span>
    </div>



    <div className="action-slot">
<button
  className="icon-btn"
  onClick={() => {
    setShowCommentsPopup(true);
  }}
>
  <img src={commentIcon} alt="Comment" className="action-icon" />
</button>



<span
  className={`action-count ${(comments.length > 0 || commentsCount > 0 || cmtDelta > 0) ? 'show-count' : ''}`}
  onClick={() => {
    setShowCommentsPopup(true);
  }}
>
  {displayedCommentsCount}
</span>



    </div>

<div className="action-slot">
  <button className="icon-btn" onClick={() => setShowTipPopup(true)}>
    <img src={tipIcon} alt="Tip" className="action-icon tip-icon-large" />
  </button>

  <span
    className={`action-count ${tipCount > 0 ? 'show-count' : ''}`}
    aria-hidden={tipCount === 0}
    onClick={async () => {
      if (tipCount === 0) return; // no drawer when 0
      if (tipList.length === 0) {
        try {
          const res = await api.get(`/tips/post/${id}`);
          setTipList(res.data.tips || []);
        } catch (err) {
          console.error('❌ Failed to fetch tips:', err);
        }
      }
      setShowTipListDrawer(true);
    }}
  >
    {tipCount || 0}
  </span>
</div>



<div
  className="action-slot view-action"
  aria-label={`${displayedViewCount} ${
    displayedViewCount === 1 ? 'view' : 'views'
  }`}
>
  <img
    src={viewCountIcon}
    alt=""
    aria-hidden="true"
    className="action-icon viewcount-icon"
  />


<span className="action-count show-count">
  {formatViewCount(displayedViewCount)}
</span>

</div>


  </div>

  {/* Right group: fixed to far right */}
  <div className="actions-right">
    <div className="action-slot">
      <button className="icon-btn" onClick={() => setShowShareDrawer(true)}>
        <img src={shareIcon} alt="Share" className="action-icon" />
      </button>
    </div>
    <div className="action-slot">
      <button className="icon-btn" onClick={handleSavePost}>
        <img
          src={saved ? saveIconFilled : saveIcon}
          alt="Save"
          className="action-icon save-icon"
        />
      </button>
    </div>
  </div>
</div>


  </div>
</div>


{previewOpen && mediaItems.length > 0 && (
  <div className="fs-backdrop" role="dialog" aria-modal="true">
    {/* Fixed top bar */}
    <div className="fs-topbar">
      <button
        type="button"
        className="fs-back-btn"
        aria-label="Back"


onClick={() => {

try { previewVideoRef.current?.pause(); } catch {}
  closingFromButtonRef.current = true; // so we ignore our own popstate
  setPreviewOpen(false);
  setFsAnimating(false);
  setFsNextIndex(null);
  setFsSwipeDir(0);
  // if we pushed a dummy history entry, consume it so the stack stays clean
  if (pushedFsHistoryRef.current && window.history.state?.bfFsPreview) {
    try { window.history.back(); } catch {}
  }
  // re-center current image in grid...

          const imgPos = imageIndices.indexOf(previewIndex);
          if (hasOnlyImages && imgPos !== -1) {
            setCurrentImageIndex(imgPos);
            const el = carouselRef.current;
            if (el && el.children && el.children[imgPos]) {
              const target = el.children[imgPos];
              el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
            }
          }
        }}
      >
        <img src={backIcon} alt="" />
      </button>

      {/* Owner chip */}
      <div
        className="fs-owner"
        role="button"
        tabIndex={0}

onClick={() => {
   try { previewVideoRef.current?.pause(); } catch {}
   setPreviewOpen(false);
   saveHomeScrollPos();
   navigate(`/user/${username}`);
 }}

        onKeyDown={(e) => {


if (e.key === 'Enter') {
   try { previewVideoRef.current?.pause(); } catch {}
   setPreviewOpen(false);
   saveHomeScrollPos();
   navigate(`/user/${username}`);
 }

        }}
      >


<img
  src={fallbackPic}
  alt={`${displayName}'s avatar`}
  className={`fs-owner-avatar ${authorIsAdmin || authorIsApp ? 'fs-owner-avatar-admin' : ''}`}
  draggable={false}
/>


      <div className="fs-owner-meta">

<div className="fs-owner-name">
  <span className="name-with-admin">
    <span>{displayName}</span>
  

{(authorIsAdmin || authorIsApp) && (
  <img
    src={goldTickIcon}
    alt={authorIsAdmin ? 'Admin verified' : 'App verified'}
    className="admin-gold-tick"
    title={authorIsAdmin ? (authorAdminRole || 'Admin') : 'App account'}
  />
)}

  </span>
</div>

        </div>
      </div>
    </div>

    {/* Media area below the bar */}
    <div
      className="fs-media"
      onTouchStart={handleFSTouchStart}
      onTouchEnd={handleFSTouchEnd}
    >
      {mediaItems[previewIndex]?.type === 'video' ? (
        <video
          ref={previewVideoRef}
          className="fs-video"
          controls
          autoPlay
          playsInline
        >
          <source src={mediaItems[previewIndex]?.url} type="video/mp4" />
        </video>
      ) : (
        <>

          {/* Current (exiting) slide */}

<img
            key={`curr-${previewIndex}`}
            src={mediaItems[previewIndex]?.url}
            alt="Fullscreen current"
            className={[
              'fs-img',
              'fs-slide',
              fsAnimating
                ? (fsSwipeDir === +1 ? 'exit-to-left' : 'exit-to-right')
                : 'at-rest'
            ].join(' ')}
            draggable={false}
            onAnimationEnd={() => {
              if (!fsAnimating) return;
              setPreviewIndex(fsNextIndex);
              setFsAnimating(false);
              setFsNextIndex(null);
              setFsSwipeDir(0);
            }}
          />

          {/* Next (entering) slide — only during animation */}
          {fsAnimating && fsNextIndex != null && (
            
<img
              key={`next-${fsNextIndex}`}
              src={mediaItems[fsNextIndex]?.url}
              alt="Fullscreen next"
              className={[
                'fs-img',
                'fs-slide',
                fsSwipeDir === +1 ? 'enter-from-right' : 'enter-from-left'
              ].join(' ')}
              draggable={false}
            />


          )}
        </>
      )}
    </div>

    {showAvatarStudioCta && (
      <div className="fs-avatar-studio-footer">
        <button
          type="button"
          className="fs-avatar-studio-button"
          disabled={fsAnimating}
          onClick={handleCreateStudioAvatar}
        >
          Create your avatar
        </button>
      </div>
    )}
  </div>
)}



{showLikesPopup && (
  <LikeDrawer
    postId={id}
    initialLikes={likes}      // optional seed; fine if empty

    onClose={handleCloseLikesPopup}
    currentUser={currentUser}
  />
)}


{showCommentsPopup && (
  <CommentDrawer
    onClose={handleCloseCommentsPopup}
    comments={comments}
    newComment={newComment}
    setNewComment={setNewComment}
    handleComment={handleComment}
    handleLikeComment={handleLikeComment}
    replyingTo={replyingTo}
    setReplyingTo={setReplyingTo}


postId={id}
currentUser={currentUser}
currentUserProfilePic={currentUserProfilePic}
postOwnerUsername={username}

    onCommentDeleted={(deletedId) => {
      // remove only the deleted comment locally (no refetch)
      setComments(prev =>
        prev.filter(c => (c.id || c._id || c.commentId) !== deletedId)
      );
    }}
  />
)}


{showDeleteConfirm && (
  <DeletePostDrawer
    isOpen={showDeleteConfirm}
    onClose={() => setShowDeleteConfirm(false)}
    onConfirm={confirmDelete}
  />
)}

{showTxnPopup && (
  <TxnDrawer
    onClose={() => setShowTxnPopup(false)}
    username={username}
    currentUser={currentUser}
    transactionId={transactionId}
  />
)}

{tipMessage && (
  <div className={`tip-banner ${tipType}`}>
    {tipMessage}
  </div>
)}




{showTipPopup && (
  <TipDrawer
    defaultAmount={autoOpenTipDrawer ? autoOpenTipAmount : 0.5}
    onClose={handleCloseTipPopup}
    onSend={async ({ amount, message }) => {

      try {
        const payload = {
          tipper: currentUser,
          postId: id,
          amount,
          message,
        };

        const res = await api.post(`/tips/send?network=${effectiveNetwork}`, payload);
        setTipList(prev => [...prev, res.data.tip]);
        setTipCount(prev => prev + 1);

        if (currentUser !== username) {
          await api.post('/notify', {
            type: 'tip',
            fromUser: currentUser,
            toUser: username,
            postId: id,
            text: `${currentUser} tipped your post with ${amount} Pi`
          });
        }

    showTimedTipBanner(`Tip of ${amount} Pi sent successfully!`, 'success', 2000);

      } catch (err) {
        console.error('❌ Tip failed:', err);
        console.log('💥 Full tip error response:', err?.response?.data);

        const errorMsg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          '❌ Failed to send tip. Try again.';

        const lowerMsg = errorMsg.toLowerCase();

if (lowerMsg.includes('you do not have') && lowerMsg.includes('wallet')) {
  // 🔴 Sender does not have a wallet
  showTimedTipBanner('You do not have an active wallet to create tip.', 'error', 2000);

  if (quickTopUpTimerRef.current) {
    clearTimeout(quickTopUpTimerRef.current);
  }

  quickTopUpTimerRef.current = setTimeout(() => {
    setShowQuickTopUp(true);
  }, 2500);
        } else if (
          lowerMsg.includes('cannot receive tips') ||
          lowerMsg.includes('recipient') ||
          lowerMsg.includes('to receive')
        ) {
          // 🔴 Recipient does not have a wallet

showTimedTipBanner(`@${username} does not have an active wallet to receive tips.`, 'error', 2000);
          // ✅ NEW: Create tip intent notification (with cooldown to avoid spam)
          try {
            if (currentUser && username && id && currentUser !== username) {
              const cooldownMs = 10 * 60 * 1000; // 10 minutes
              const key = `bf_tip_intent_${currentUser}_${username}_${id}`;
              const last = Number(localStorage.getItem(key) || '0');

              if (!last || Date.now() - last > cooldownMs) {
                await api.post('/notify', {
                  type: 'tip_intent',
                  fromUser: currentUser,
                  toUser: username,   // post owner
                  postId: id,
                  text: `${currentUser} wanted to tip your post`,
                }).catch(() => {});

                localStorage.setItem(key, String(Date.now()));
              }
            }
          } catch (e) {
            // silent fail
          }

} else if (
  lowerMsg.includes('insufficient') ||
  lowerMsg.includes('not enough')
) {
  showTimedTipBanner('Not enough Pi to send this tip.', 'error', 2000);

  if (quickTopUpTimerRef.current) {
    clearTimeout(quickTopUpTimerRef.current);
  }

  quickTopUpTimerRef.current = setTimeout(() => {
    setShowQuickTopUp(true);
  }, 2500);
  
      } else {

showTimedTipBanner(errorMsg, 'error', 2000);
        }
      }

    }}
  />
)}



{showTipListDrawer && (
  <TipListDrawer
 
    onClose={handleCloseTipListDrawer}
    tips={tipList}
    currentUser={currentUser}
  />
)}

{showReportPopup && (
  <ReportPopup
    onClose={() => setShowReportPopup(false)}
    onSubmit={async (payload) => {
      try {
        await api.post('/reports', payload);
        setShowReportPopup(false);
        showReportToast(' Report submitted successfully!', 'success');
      } catch (err) {
        console.error('❌ Report failed:', err);
        setShowReportPopup(false);
        showReportToast('❌ Failed to submit report. Please try again.', 'error');
      }
    }}
    reportedId={id}
    reporterUsername={currentUser}
    type="post"
  />
)}


<PostActionDrawer
  isOpen={showActionDrawer}
  onClose={() => setShowActionDrawer(false)}
  isOwner={isOwner}
  isFollowing={isFollowing}
  followsMe={followsMe}
  targetUsername={username}
  isBlocked={isBlocked}
  blockActionLoading={blockActionLoading}
  onToggleFollow={async () => { await toggleFollow(); }}
  onToggleBlock={handleToggleBlock}
  onReport={() => setShowReportPopup(true)}
  onDelete={() => setShowDeleteConfirm(true)}

  onEdit={() => {
    setShowActionDrawer(false);
    saveHomeScrollPos();
    navigate(`/posts/${id}/edit`);
  }}

  pinned={isPinned}
  onPinToggle={handlePinToggle}

  showPinControls={viewerIsAdmin}
  showStakeOption={canStakeToBoost}
  onStakeClick={() => {
    setShowActionDrawer(false);
    setTimeout(() => { openStakeDrawer(); }, 0);
  }}
/>


<BlockConfirmDrawer
  isOpen={showBlockConfirmDrawer}
  mode={isBlocked ? 'unblock' : 'block'}
  targetUsername={username}
  loading={blockActionLoading}
  onClose={() => {
    if (!blockActionLoading) {
      setShowBlockConfirmDrawer(false);
    }
  }}
  onConfirm={confirmToggleBlock}
/>


{showStakeDrawer && (
  <StakeDrawer
    isOpen={showStakeDrawer}
    onClose={() => setShowStakeDrawer(false)}
    postId={id}
    defaultAmount={5}
    defaultDays={2}
    currentUser={currentUser}
    effectiveNetwork={effectiveNetwork}
    onSuccess={({ stake, post, summary }) => {
      const nextTotal =
        typeof summary?.stakeActivePi === 'number' ? summary.stakeActivePi
        : typeof post?.stakeActivePi === 'number' ? post.stakeActivePi
        : Number(stakeUi.activePi || 0) + Number(stake?.amountPi || 0);

      setStakeUi({
        activePi: nextTotal,
        nextExpiry:
          summary?.stakeNextExpiryAt ||
          post?.stakeNextExpiryAt ||
          stakeUi.nextExpiry ||
          null,
      });

      setShowStakeDrawer(false);
    }}
  />
)}


{showQuickTopUp && (
  <QuickTopUpPopup
    open={showQuickTopUp}
    user={{ username: currentUser }}
    onClose={() => setShowQuickTopUp(false)}

onSuccess={() => {
  setShowQuickTopUp(false);
  showTimedTipBanner('Wallet topped up successfully.', 'success', 2000);
}}

  />
)}


{showShareDrawer && (
  <ShareDrawer
    isOpen={showShareDrawer}
    onClose={() => setShowShareDrawer(false)}
    postId={id}
    sender={currentUser}
    onShared={() => {
      console.log('✅ Post shared!');
      // Optional: toast or visual feedback
    }}
  />
)}


{reportToast.visible && (
  <div className={`post-toast ${reportToast.type}`}>
    {reportToast.message}
  </div>
)}


</>

); // closes the return

} // ✅ closes the Post function

export default Post;
